/**
 * Numbers the panels and dialogs show that belong to later tasks' data files (M3 plan R16): the scanner ranges and
 * the first-discovery multiplier from `reach-explore.json` (R18), and the reboot duration per region from
 * `reach-relink.json` (R19). The files are found with `import.meta.glob`, so a file that has not landed yet is
 * simply absent and the UI shows "—" (or a duration-free sentence) instead of guessing a number. Only the fields
 * read here are validated; the owners' own schemas validate the rest.
 */
import { z } from 'zod'
import { logger } from '../../../core/logger'
import type { RegionId } from '../../../galaxy/types'

const OPTIONAL_DATA = import.meta.glob<unknown>(['/src/data/reach-explore.json', '/src/data/reach-relink.json'], {
  eager: true,
  import: 'default',
})

const exploreSchema = z.object({
  scanner: z.object({ radiusM: z.array(z.number().positive().nullable()).length(3) }),
  charts: z.object({ firstDiscoveryMult: z.number().positive() }),
})

const relinkSchema = z.object({
  reboot: z.object({ seconds: z.partialRecord(z.enum(['gate', 'mid', 'deep', 'rim']), z.number().positive()) }),
})

export interface ServicesExternals {
  /** Deep-scan range per scanner Mk (index Mk − 1), metres; null = the whole system. Null until R18's file exists. */
  scannerRangesM: readonly (number | null)[] | null
  /** The chart valuation's first-discovery multiplier. Null until R18's file exists. */
  firstDiscoveryMult: number | null
  /** Reboot defence seconds per region. Null until R19's file exists. */
  rebootSeconds: Partial<Record<RegionId, number>> | null
}

function parsed<T>(schema: z.ZodType<T>, raw: unknown, file: string): T | null {
  if (raw === undefined) return null
  const r = schema.safeParse(raw)
  if (r.success) return r.data
  logger.warn(`Station services: ${file} does not have the expected fields`, r.error.issues)
  return null
}

/** Reads the optional files (`raw` maps '/src/data/<file>' to its parsed JSON; tests pass their own). */
export function loadServicesExternals(raw: Readonly<Record<string, unknown>> = OPTIONAL_DATA): ServicesExternals {
  const explore = parsed(exploreSchema, raw['/src/data/reach-explore.json'], 'reach-explore.json')
  const relink = parsed(relinkSchema, raw['/src/data/reach-relink.json'], 'reach-relink.json')
  return {
    scannerRangesM: explore?.scanner.radiusM ?? null,
    firstDiscoveryMult: explore?.charts.firstDiscoveryMult ?? null,
    rebootSeconds: relink?.reboot.seconds ?? null,
  }
}
